import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { CheckCircle } from 'lucide-react';
import { products, companyInfo } from '../mockData';

export const ProductsPage = () => {
  return (
    <div className="min-h-screen pt-28 pb-20">
      {/* Hero Section with Background */}
      <section className="relative px-4 sm:px-6 lg:px-8 mb-20 py-20">
        <div className="absolute inset-0 -z-10">
          <img
            src={companyInfo.heroImage}
            alt="Industrial Facility"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.3) contrast(1.1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#004494]/90 via-[#004494]/80 to-white" />
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative">
          <h1 className="text-5xl font-bold text-white mb-6 font-heading drop-shadow-2xl">
            Products & Services
          </h1>
          <p className="text-xl text-gray-100 max-w-3xl mx-auto leading-relaxed drop-shadow-lg">
            Comprehensive range of electrical control panels and solutions engineered to meet the highest industrial standards
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-16">
          {products.map((product, index) => (
            <Card
              key={product.id}
              className="overflow-hidden border-2 hover:border-[#004494] transition-all duration-300 shadow-lg"
            >
              <div
                className={`grid lg:grid-cols-2 gap-8 ${
                  index % 2 === 1 ? 'lg:grid-flow-dense' : ''
                }`}
              >
                {/* Image */}
                <div
                  className={`relative h-80 lg:h-auto overflow-hidden ${
                    index % 2 === 1 ? 'lg:col-start-2' : ''
                  }`}
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#004494]/60 to-transparent" />
                </div>

                {/* Content */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="inline-block mb-4">
                    <span className="text-sm font-semibold text-[#004494] bg-[#FFD617]/20 px-4 py-2 rounded-full">
                      Product #{product.id}
                    </span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">
                    {product.name}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Features Accordion */}
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="features">
                      <AccordionTrigger className="text-lg font-semibold text-[#004494] hover:text-[#003370]">
                        Technical Specifications
                      </AccordionTrigger>
                      <AccordionContent>
                        <ul className="space-y-3 pt-2">
                          {product.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-3">
                              <CheckCircle className="w-5 h-5 text-[#004494] mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8 mt-20">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-gradient-to-br from-gray-50 to-white border-2 border-[#004494]">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold text-gray-900 mb-4 font-heading">
                Need a Custom Solution?
              </h3>
              <p className="text-lg text-gray-600 mb-8">
                Our engineering team can design and manufacture control panels tailored to your specific requirements.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-[#004494] hover:bg-[#003370] text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                Contact Our Engineers
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};